import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save } from 'lucide-react';
import { findTagOverlap } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { TagRuleEditor } from './BatchActionsSettingsSection';

type TagLists = { dtf: string[]; dtg: string[] };

const sameTags = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((tag, i) => tag === b[i]);

/**
 * BLU-1135 / BLU-1410: the org's DTF and DTG order tags. An order carrying any
 * one tag of a list prints every print-on-demand item with that technology,
 * decided when the order is batched; an order carrying a tag of each is not
 * batched. Own org only; the lists apply to every order made at this org's
 * floors. A tag can be in one list only (matching ignores case and outer
 * spaces), so Save waits until the lists share none.
 */
export function DtfOrderTagsSection() {
  const { t } = useTranslation('settings');
  const { can } = useRole();
  const canManage = can('production_settings.manage');
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<TagLists | null>(null);

  const settings = trpc.productionSettings.getOrderTechnologyTags.useQuery(undefined, { enabled: canManage });
  const save = trpc.productionSettings.updateOrderTechnologyTags.useMutation({
    onSuccess: async () => {
      // Nothing else reads these lists on the client; refetch only them.
      await utils.productionSettings.getOrderTechnologyTags.invalidate();
      setDraft(null);
      toast.success(t('dtfOrderTags.saved'));
    },
    onError: (error) => toast.error(t('dtfOrderTags.saveError', { message: error.message }), { duration: Infinity }),
  });

  if (!canManage) return null;
  if (settings.isPending) {
    return (
      <div className="space-y-3" role="status" aria-label={t('dtfOrderTags.loading')}>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (!settings.data) {
    return (
      <SectionError
        variant="compact"
        body={t('dtfOrderTags.loadError')}
        onRetry={() => settings.refetch()}
        retryLabel={t('dtfOrderTags.retry')}
        retrying={settings.isFetching}
      />
    );
  }

  const saved = settings.data;
  const lists = draft ?? saved;
  const changed = draft !== null && !(sameTags(draft.dtf, saved.dtf) && sameTags(draft.dtg, saved.dtg));
  const overlap = findTagOverlap(lists.dtf, lists.dtg);
  const empty = lists.dtf.length === 0 && lists.dtg.length === 0;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5 max-w-prose">
        <p className="text-xs text-muted-foreground">{t('dtfOrderTags.timing')}</p>
        <p className="text-xs text-muted-foreground">{t('dtfOrderTags.clientHelp')}</p>
      </div>

      <fieldset disabled={save.isPending} className="space-y-2">
        <legend className="text-sm font-medium">{t('dtfOrderTags.dtfLabel')}</legend>
        <p className="text-sm text-muted-foreground max-w-prose">{t('dtfOrderTags.description')}</p>
        <TagRuleEditor
          actionId="dtf-orders"
          tags={lists.dtf}
          onChange={(dtf) => setDraft({ ...lists, dtf })}
          help={t('dtfOrderTags.matchHelp')}
          caseInsensitive
        />
      </fieldset>

      <fieldset disabled={save.isPending} className="space-y-2">
        <legend className="text-sm font-medium">{t('dtfOrderTags.dtgLabel')}</legend>
        <p className="text-sm text-muted-foreground max-w-prose">{t('dtfOrderTags.dtgDescription')}</p>
        <TagRuleEditor
          actionId="dtg-orders"
          tags={lists.dtg}
          onChange={(dtg) => setDraft({ ...lists, dtg })}
          help={t('dtfOrderTags.matchHelp')}
          caseInsensitive
        />
      </fieldset>

      {overlap.length > 0 && (
        <p className="text-sm text-destructive" role="alert">
          {t('dtfOrderTags.overlap', { tags: overlap.join(', ') })}
        </p>
      )}
      {empty && <p className="text-sm text-muted-foreground">{t('dtfOrderTags.empty')}</p>}

      <div className="flex flex-wrap gap-2">
        <Button
          className="h-8 gap-1.5"
          disabled={!changed || overlap.length > 0 || save.isPending}
          onClick={() => save.mutate({ dtf: lists.dtf, dtg: lists.dtg })}
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {t('dtfOrderTags.save')}
        </Button>
        <Button variant="outline" className="h-8" disabled={save.isPending || empty} onClick={() => setDraft({ dtf: [], dtg: [] })}>
          {t('dtfOrderTags.clear')}
        </Button>
      </div>
    </div>
  );
}
